import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import useReportLog from '../../../../hooks/useReportLog';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import ReportChart from '../reportChart';
import {
  buildFuelConsSchema,
  buildFuelConsTable,
  createRunningTimeAt,
  getAggregatedLength,
  PdfChartData,
} from '../../pdf/reportPdfData';
import { Segmented, Select } from 'antd';
import styled from 'styled-components';
import { formatChartTables } from './chartTableHeader';

interface IProps {
  chartType: string;
  isReportSchedule?: {
    startAt: number;
    endAt: number;
    interval: any;
    deviceType: string;
    assetId: string;
    timezone: number;
  };
  height?: number;
  compactControls?: boolean;
  // calculated by the backend for the PDF page (GET /report-log/pdf-data)
  precomputed?: PdfChartData;
}

const ChartFuelCons: React.FC<IProps> = ({
  chartType,
  isReportSchedule,
  height,
  compactControls,
  precomputed,
}) => {
  const { logs } = useReportLog();
  const { filter, asset, timezone } = useAppliedReportFilter();
  const [activeChartType, setActiveChartType] = useState<string>('default');
  const reportTimezone = isReportSchedule?.timezone ?? timezone;

  const schema = useMemo(() => {
    if (precomputed) {
      return precomputed.schema;
    }

    return buildFuelConsSchema({
      logs,
      chartType,
      activeChartType,
      isReportSchedule: !!isReportSchedule,
      assetDevices: asset.devices,
      assetType: asset.assetType,
    });
  }, [
    precomputed,
    chartType,
    activeChartType,
    asset.devices,
    asset.assetType,
    logs,
    isReportSchedule,
  ]);

  const dataTable = useMemo(() => {
    if (precomputed) {
      return { ...precomputed.table, runningLookup: null };
    }

    return buildFuelConsTable({
      logs,
      schema,
      schedule: isReportSchedule,
      timezone: reportTimezone,
    });
  }, [precomputed, schema, logs, isReportSchedule, reportTimezone]);

  const tables = useMemo(
    () =>
      formatChartTables(
        dataTable,
        isReportSchedule?.startAt as number,
        reportTimezone
      ),
    [dataTable, isReportSchedule?.startAt, reportTimezone]
  );

  const runningTimeAt = useMemo(
    () => createRunningTimeAt(dataTable.runningLookup),
    [dataTable.runningLookup]
  );

  const tableColumns = useMemo(() => {
    let res: any[] = ['Time'];
    schema.forEach((item) => {
      res.push(item.seriesOption.name);
    });

    return res;
  }, [schema]);

  const aggregatedLength = useMemo(() => {
    if (isReportSchedule) {
      return getAggregatedLength({
        startAt: isReportSchedule.startAt,
        endAt: isReportSchedule.endAt,
        interval: isReportSchedule.interval,
      });
    } else {
      return getAggregatedLength({
        startAt: filter.startAt,
        endAt: filter.endAt,
        interval: filter.interval,
      });
    }
  }, [filter, schema, isReportSchedule]);

  return (
    <>
      {!isReportSchedule && (
        <ChartTypeControl $compact={compactControls}>
          {compactControls ? (
            <MobileChartMode
              block
              value={activeChartType}
              onChange={(value) => setActiveChartType(String(value))}
              options={[
                { label: 'Default', value: 'default' },
                { label: 'Line Chart', value: 'line' },
              ]}
            />
          ) : (
            <Select
              value={activeChartType}
              style={{ width: 120 }}
              placeholder='Chart type'
              onChange={(value) => setActiveChartType(value)}
              options={[
                { label: 'Default', value: 'default' },
                { label: 'Line', value: 'line' },
              ]}
            />
          )}
        </ChartTypeControl>
      )}
      <ReportChart
        height={height}
        logs={logs}
        filter={isReportSchedule || filter}
        yAxis={
          chartType === 'me'
            ? ['fuel', 'rpm', 'speed']
            : ['fuel', 'rpm', 'time']
        }
        chartSchema={schema}
        isReportSchedule={
          isReportSchedule && isReportSchedule.deviceType ? true : false
        }
        aggregatedLength={aggregatedLength}
        compact={compactControls}
        runningTimeAt={runningTimeAt}
        precomputedSeries={precomputed?.series}
      />

      {isReportSchedule && tableColumns.length && (
        <>
          {tables.map((table, tableIdx) => (
            <div key={tableIdx} style={{ marginTop: tableIdx > 0 ? '14px' : '0' }}>
              {dataTable.is48HourMode && isReportSchedule && (
                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '7px', marginBottom: '2px' }}>
                  {dayjs(isReportSchedule.startAt * 1000)
                    .utcOffset(reportTimezone * 60)
                    .add(tableIdx, 'day')
                    .format('DD MMM YYYY')}
                </div>
              )}
              <TableSummary columnCount={dataTable.columnCount} is48HourMode={dataTable.is48HourMode}>
                <tbody>
                  {table.map((data, idx) => (
                    <tr key={idx}>
                      <td>{tableColumns[idx]}</td>
                      {data.map((item: number | string | undefined, idx2: number) => (
                        <td key={idx2}>
                          {typeof item === 'undefined' || item === null
                            ? '-'
                            : idx === 0
                            ? item
                            : typeof item === 'number'
                            ? item.toFixed(2)
                            : item}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </TableSummary>
            </div>
          ))}
        </>
      )}
    </>
  );
};

const TableSummary = styled.table<{ columnCount: number; is48HourMode: boolean }>`
  width: 100%;
  margin: ${props => props.is48HourMode ? '5px auto 0' : '0 auto'};
  table-layout: fixed;
  font-size: ${props => props.is48HourMode ? '6px' : props.columnCount > 10 ? '8px' : '10px'};
  td {
    border: thin solid #ddd;
    padding: ${props => props.is48HourMode ? '1px' : props.columnCount > 10 ? '4px 1px' : '6px 2px'};
    font-size: inherit;
    text-align: center;
  }
  tr:first-child {
    background: #d1d1d1;
  }
  td:first-child {
    width: ${props => props.is48HourMode ? '45px' : props.columnCount > 10 ? '60px' : '80px'};
    background: #142d79;
    color: white;
    border-color: #142d79;
  }
`;

const ChartTypeControl = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: ${({ $compact }) => ($compact ? 'stretch' : 'flex-start')};
  width: ${({ $compact }) => ($compact ? '100%' : 'auto')};
  padding: 0;
`;

const MobileChartMode = styled(Segmented)`
  width: 100%;
  padding: 4px;
  border: 1px solid #e8ecf3;
  border-radius: 12px;
  background: #e8ecf3;

  .ant-segmented-item {
    min-height: 41px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    color: #1e1e1e;
    font-size: 14px;
    line-height: 1.2;
  }

  .ant-segmented-item-label {
    min-height: 41px;
    line-height: 41px;
    padding: 0 16px;
  }

  .ant-segmented-item-selected {
    border-radius: 8px;
    color: #1e1e1e;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }

  .ant-segmented-thumb {
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin: 20px 0 10px 0;
  padding: 8px;
  background: #f5f5f5;
  border-left: 4px solid #142d79;
`;

export default ChartFuelCons;
